// ═══════════════════════════════════════════════════════════════════
// SHORTCUTS HELP — Display available keyboard shortcuts
// Grid layout showing all keyboard shortcuts with descriptions
// Triggered by ? or help button
// ═══════════════════════════════════════════════════════════════════
import { type ThemeObject, GLASS, TRANSITION, SP, RADIUS } from "../shared/theme.js";

interface ShortcutsHelpProps {
  T: ThemeObject;
  onClose: () => void;
}

const SHORTCUTS = [
  { key: "Ctrl + K", label: "Cmd + K", description: "Abrir paleta de comandos" },
  { key: "← / →", label: "Setas", description: "Navegar entre páginas" },
  { key: "1-9", label: "Números", description: "Ir para página por posição" },
  { key: "?", label: "Interrogação", description: "Mostrar este painel" },
  { key: "Esc", label: "Escape", description: "Fechar sobreposições" },
];

export default function ShortcutsHelp({ T, onClose }: ShortcutsHelpProps) {
  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          zIndex: 9998,
          animation: "fade-in 0.2s ease-out",
        }}
        onClick={onClose}
      />

      {/* Help modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          width: "90%",
          maxWidth: 540,
          background: GLASS.dark.bg,
          backdropFilter: GLASS.dark.blur,
          border: `1px solid ${GLASS.dark.border}`,
          borderRadius: RADIUS.lg,
          boxShadow: GLASS.dark.shadow,
          animation: "palette-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          fontFamily: "'Inter', sans-serif",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: SP[6],
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 700,
              color: T.text,
            }}
          >
            Atalhos de Teclado
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: T.muted,
              fontSize: "24px",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              transition: TRANSITION.fast,
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.color = T.text;
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.color = T.muted;
            }}
          >
            ✕
          </button>
        </div>

        {/* Shortcuts grid */}
        <div
          style={{
            padding: SP[6],
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: SP[4],
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {SHORTCUTS.map((shortcut, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: SP[2],
              }}
            >
              {/* Key badge */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: SP[2],
                  flexWrap: "wrap",
                }}
              >
                {shortcut.key.split("+").map((part, i) => (
                  <span
                    key={i}
                    style={{
                      display: "inline-block",
                      background: T.surface,
                      color: T.text,
                      padding: `${SP[1]} ${SP[3]}`,
                      borderRadius: RADIUS.sm,
                      fontSize: "11px",
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace",
                      border: `1px solid ${T.border}`,
                    }}
                  >
                    {part.trim()}
                  </span>
                ))}
              </div>

              {/* Description */}
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: T.sub,
                  lineHeight: 1.4,
                }}
              >
                {shortcut.description}
              </p>
            </div>
          ))}
        </div>

        {/* Footer info */}
        <div
          style={{
            padding: SP[4],
            borderTop: `1px solid ${T.border}`,
            fontSize: "11px",
            color: T.muted,
            textAlign: "center",
          }}
        >
          Pressione ? para fechar | Clique fora para fechar
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes palette-in {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}</style>
    </>
  );
}
